import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Loading } from '@pandiafi/design-system'
import { useNavigate } from 'react-router-dom'

import { ConfirmOperationType, Id } from '../../../../common/types'
import { ResidentAppLocale } from '../../../../common/validations'
import { useAPIConfirmApproveModification } from '../../../api/apiHooks/useAPIConfirmApproveModification'

import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import i18n from 'i18n/i18n'

interface ConfirmContractModificationFormProps {
  children: React.ReactNode
  contractModificationRequestId: Id
  userMetadata: { isImpersonator: boolean }
}

export const ConfirmContractModificationForm: FC<
  ConfirmContractModificationFormProps
> = ({ children, contractModificationRequestId, userMetadata }) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['contractModification'])
  const { currentContractId: contractUuid } = useCurrentContract()

  const lang = i18n.language ?? ResidentAppLocale.FI

  const { mutate, isSuccess, data, isPending } =
    useAPIConfirmApproveModification(contractUuid, lang)

  const handleConfirm = () =>
    mutate({
      operationData: {
        terminationRequestId: contractModificationRequestId,
      },
      operationType:
        ConfirmOperationType.RESIDENT_APP_ACCEPT_CONTRACT_MODIFICATION,
    })

  const handleReject = () =>
    mutate({
      operationData: {
        terminationRequestId: contractModificationRequestId,
      },
      operationType:
        ConfirmOperationType.RESIDENT_APP_REJECT_CONTRACT_MODIFICATION,
    })

  const handleBack = () => {
    navigate(-1)
  }

  if (isSuccess && data && data.link) {
    window.location.href = data.link
  }

  return (
    <>
      {children}
      <div className="flex justify-center">
        <div className="inline-flex flex-col space-y-4">
          {isPending && <Loading />}

          <Button
            variant="delete"
            type="button"
            onClick={handleConfirm}
            disabled={isPending || userMetadata.isImpersonator}
          >
            {t('contractModification:buttons.confirmModification')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleReject}
            disabled={isPending || userMetadata.isImpersonator}
          >
            {t('contractModification:buttons.rejectModification')}
          </Button>
          <Button variant="text" onClick={handleBack}>
            {t('contractModification:buttons.back')}
          </Button>
        </div>
      </div>
    </>
  )
}
